import { ProjectValidationUnit } from '../../../../models/data-structures/project-validation';
import {
  FeedstockFinancial,
  FeedstockFinancialPropName
} from '../../../../models/project/feedstock/feedstock-financial.model';
import { FinancialFeeType } from '../../../../models/project/financial/financial-fee-type.enum';
import { UnitValueValidation } from '../unit-value.validation';
import { ValidationInterface } from '../../validation.interface';

export class FinancialFeedstockValidation implements ValidationInterface {
  /** The current feedstock financial obj that is being validated */
  private readonly feedstockFin: FeedstockFinancial;

  /**
   * Sets the feedstock financial obj that will be used for validation
   * @param feedstockFin will be used to set the current feedstock financial obj
   */
  constructor(feedstockFin: FeedstockFinancial) {
    if (!feedstockFin) {
      throw Error('invalid feedstock for validation');
    }
    this.feedstockFin = new FeedstockFinancial(feedstockFin);
  }


  /**
   * Get a validation unit containing the validation data for the current feedstock financial obj
   */
  getValidation(): ProjectValidationUnit {
    if (!this.feedstockFin) throw Error('unable to validate feedstock financial');
    const validationUnit = new ProjectValidationUnit();
    if (this.feedstockFin.feeType) {
      validationUnit.setValid(FeedstockFinancialPropName.feeType);
    } else {
      validationUnit.setInvalid(
        FeedstockFinancialPropName.feeType,
        [FinancialFeedstockErrorMessage.NO_FEE_TYPE_SELECTED]
      );
    }
    if (this.feedstockFin.feeType !== FinancialFeeType.FREE) {
      validationUnit.addModuleResults(
        new UnitValueValidation(this.feedstockFin.price).getValidation(),
        FeedstockFinancialPropName.price
      );
      validationUnit.addModuleResults(
        new UnitValueValidation(this.feedstockFin.amount).getValidation(),
        FeedstockFinancialPropName.amount
      );
      validationUnit.addModuleResults(
        new UnitValueValidation(this.feedstockFin.feedstockCpi).getValidation(),
        FeedstockFinancialPropName.feedstockCpi
      );
    }
    if (this.feedstockFin.useTransport) {
      validationUnit.addModuleResults(
        new UnitValueValidation(this.feedstockFin.transportPrice).getValidation(),
        FeedstockFinancialPropName.transportPrice
      );
      validationUnit.addModuleResults(
        new UnitValueValidation(this.feedstockFin.transportAmount).getValidation(),
        FeedstockFinancialPropName.transportAmount
      );
      validationUnit.addModuleResults(
        new UnitValueValidation(this.feedstockFin.transportCpi).getValidation(),
        FeedstockFinancialPropName.transportCpi
      );
    }
    return validationUnit;
  }

  /**
   * returns a boolean that determines if the current financial feedstock obj is valid
   */
  isValid(): boolean {
    return this.getValidation().isValid;
  }

  /**
   * Determines if the feedstock financial info is valid for non transportation calculations.
   */
  validForOpexNonTransport(): boolean {
    if (!this.feedstockFin) return false;
    let isValid = true;
    if (!this.feedstockFin.feeType) isValid = false;
    isValid = isValid && new UnitValueValidation(this.feedstockFin.price).getValidation().isValid;
    isValid = isValid && new UnitValueValidation(this.feedstockFin.amount).getValidation().isValid;
    isValid = isValid && new UnitValueValidation(this.feedstockFin.feedstockCpi).getValidation().isValid;
    return isValid;
  }

  /**
   * Determines if the feedstock transportation info is valid for calculations.
   */
  validForOpexTransportExpense(): boolean {
    if (!this.feedstockFin) return false;
    let isValid = true;
    isValid = isValid && new UnitValueValidation(this.feedstockFin.transportPrice).getValidation().isValid;
    isValid = isValid && new UnitValueValidation(this.feedstockFin.transportAmount).getValidation().isValid;
    isValid = isValid && new UnitValueValidation(this.feedstockFin.transportCpi).getValidation().isValid;
    return isValid;
  }
}

/**
 * Enum for feedstock financial error messages.
 */
enum FinancialFeedstockErrorMessage {
  NO_FEE_TYPE_SELECTED = 'VALIDATION.SELECT_FEE_TYPE'
}
